import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  type ColumnOrderState,
  type ColumnSizingState,
  type ColumnVisibilityState,
  type OnChangeFn,
} from '@tanstack/react-table'
import { useDebouncedValue } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import {
  normalizeColumnOrder,
  pruneStoredState,
  readStoredState,
  writeStoredState,
} from './storedState'

const COLUMN_SIZING_PERSIST_DEBOUNCE_MS = 250

// Module-level on purpose: storage failing is a property of the browser, not of one table,
// so the warning shows once per page load however many tables run into it.
let storageFailureNotified = false

export interface ColumnPreferenceStorageKeys {
  visibility: string
  sizing: string
  order: string
}

interface UseColumnPreferencesOptions {
  storageKeys: ColumnPreferenceStorageKeys
  // Every column id the table may show, in default order. Stored order and visibility are
  // pruned to this set, so a column the viewer lost access to never resurfaces from storage.
  canonicalColumnOrder: readonly string[]
  defaultColumnVisibility?: ColumnVisibilityState
  // Unique per table, so one table's storage probe can never touch another's stored state.
  storageProbeKey: string
  // Per table, because each message names what that table will lose. Device Management's
  // includes its search mode, which is not a column preference and which other tables lack.
  storageFailureMessage: string
}

export const useColumnPreferences = ({
  storageKeys,
  canonicalColumnOrder,
  defaultColumnVisibility,
  storageProbeKey,
  storageFailureMessage,
}: UseColumnPreferencesOptions) => {
  const {
    visibility: visibilityKey,
    sizing: sizingKey,
    order: orderKey,
  } = storageKeys

  // Keyed on content rather than identity. A caller passing an inline array would otherwise
  // hand this a new reference every render, and the normalise effect below — which sets
  // state — would re-run on every render without end. For a memoised caller the two keys
  // change together, so nothing moves.
  const canonicalKey = canonicalColumnOrder.join('|')
  const stableCanonicalOrder = useMemo(() => canonicalColumnOrder, [canonicalKey])
  const allowedColumnIds = useMemo(
    () => new Set<string>(stableCanonicalOrder),
    [stableCanonicalOrder],
  )

  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>(() => ({
    ...defaultColumnVisibility,
    ...readStoredState<ColumnVisibilityState>(visibilityKey, {}),
  }))
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() =>
    readStoredState(sizingKey, {}),
  )
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(() =>
    normalizeColumnOrder(
      readStoredState<unknown>(orderKey, []),
      allowedColumnIds,
      stableCanonicalOrder,
    ),
  )
  const [storageAvailable, setStorageAvailable] = useState(true)
  const [debouncedColumnSizing] = useDebouncedValue(
    columnSizing,
    COLUMN_SIZING_PERSIST_DEBOUNCE_MS,
  )

  const reportStorageUnavailable = useCallback(() => {
    setStorageAvailable(false)

    if (!storageFailureNotified) {
      storageFailureNotified = true
      notifications.show({
        title: 'Table preferences unavailable',
        message: storageFailureMessage,
        color: 'yellow',
      })
    }
  }, [storageFailureMessage])

  useEffect(() => {
    try {
      localStorage.setItem(storageProbeKey, '1')
      localStorage.removeItem(storageProbeKey)
    } catch {
      reportStorageUnavailable()
    }
  }, [reportStorageUnavailable, storageProbeKey])

  const handleColumnVisibilityChange: OnChangeFn<ColumnVisibilityState> = useCallback((updater) => {
    setColumnVisibility((previous) => {
      const next = typeof updater === 'function' ? updater(previous) : updater
      const pruned = pruneStoredState(next, allowedColumnIds)

      if (!writeStoredState(visibilityKey, pruned)) reportStorageUnavailable()
      return next
    })
  }, [allowedColumnIds, reportStorageUnavailable, visibilityKey])

  const handleColumnSizingChange: OnChangeFn<ColumnSizingState> = useCallback((updater) => {
    setColumnSizing((previous) => typeof updater === 'function' ? updater(previous) : updater)
  }, [])

  const handleColumnOrderChange: OnChangeFn<ColumnOrderState> = useCallback((updater) => {
    setColumnOrder((previous) => {
      const next = typeof updater === 'function' ? updater(previous) : updater
      const normalized = normalizeColumnOrder(next, allowedColumnIds, stableCanonicalOrder)

      if (!writeStoredState(orderKey, normalized)) reportStorageUnavailable()
      return normalized
    })
  }, [allowedColumnIds, orderKey, reportStorageUnavailable, stableCanonicalOrder])

  useEffect(() => {
    setColumnOrder((previous) => {
      const normalized = normalizeColumnOrder(
        previous,
        allowedColumnIds,
        stableCanonicalOrder,
      )
      if (!writeStoredState(orderKey, normalized)) reportStorageUnavailable()
      return normalized
    })
  }, [allowedColumnIds, orderKey, reportStorageUnavailable, stableCanonicalOrder])

  useEffect(() => {
    const pruned = pruneStoredState(debouncedColumnSizing, allowedColumnIds)
    if (!writeStoredState(sizingKey, pruned)) reportStorageUnavailable()
  }, [allowedColumnIds, debouncedColumnSizing, reportStorageUnavailable, sizingKey])

  return {
    columnOrder,
    columnSizing,
    columnVisibility,
    handleColumnOrderChange,
    handleColumnSizingChange,
    handleColumnVisibilityChange,
    reportStorageUnavailable,
    storageAvailable,
  }
}
